import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Form } from "@/components/ui/form"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { AppDispatch } from "@/redux"
import { useDispatch } from "react-redux"
import { getFormProps } from "@/helpers/formHelpers"
import { FilterProps } from "@/types/workflow"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { FilterCode } from "@/constants/filters/filters"
import { useEffect } from "react"
import { DatePickerInputField } from "@/components/custom/InputField/DatePickerInput"

const formSchema = z.object({
  date: z.date().min(new Date(0), {
    message: 'Please select any date',
  })
})

export const DateFilter: React.FC<FilterProps> = ({
  code,
  stepId,
  order,
  selectedValue,
  name,
  description,
}: FilterProps) => {
  const dispatch: AppDispatch = useDispatch()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: new Date(),
    },
  })
  const date = form.watch('date')
  const formProps = getFormProps(form, formSchema)

  useEffect(() => {
    if (selectedValue) {
      form.setValue('date', new Date(selectedValue))
    }
  }, [selectedValue, form])

  const getMinimumCalendarDate = () => {
    const currentDate = new Date()
    const oneYearBefore = new Date(currentDate)
    oneYearBefore.setFullYear(currentDate.getFullYear() - 2)
    return oneYearBefore
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          className="w-auto font-semibold"
          variant={ButtonVariant.DEFAULT}
        >
          Update
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-6">
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col items-start">
                <h4 className="text-primary font-semibold text-xl leading-5 mb-2">
                  Filter Setup: {name}
                </h4>
                <p className="text-secondary text-sm mb-2 text-left font-normal">{description}</p>
              </div>
              <div className="flex items-center">
                <AlertDialogCancel className="mx-4">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  type="submit"
                  disabled={!date}
                  onClick={() => {
                    dispatch(workflowActions.setStepFilter({
                      stepId,
                      order,
                      filter: code,
                      filterValue: date?.toISOString()
                    }))
                    dispatch(workflowActions.setLastUpdatedFilter(code as FilterCode))
                  }}
                >
                  Update Filter
                </AlertDialogAction>
              </div>
            </div>
          </AlertDialogTitle>
          <div className="">
            <div className="my-4" />
            <Form {...form}>
              <form className="space-y-6 flex flex-col items-start">
                <DatePickerInputField
                  name="date"
                  label="Date"
                  isDisabled={false}
                  className={'w-[210px]'}
                  calendarMinDate={getMinimumCalendarDate()} // Max Date is next 2 years by default
                  {...formProps}
                />
              </form>
            </Form>
          </div>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  )
}
